import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { List } from 'foundations-components/transitional/components/List';
import { Text } from 'foundations-components/components/Text';
import { BrandingAndLimitPriorityFeatures } from 'pricing-pages-ui-components/components/merch-card/feature-display/BrandingAndLimitPriorityFeatures';
import { FeaturesDisplayLayoutUI } from 'pricing-pages-ui-components/components/merch-card/feature-display/FeaturesDisplayLayoutUI';
import { UncategorizedPriorityFeatures } from 'pricing-pages-ui-components/components/merch-card/feature-display/UncategorizedPriorityFeatures';
import { getI18nTextForFeatureItem, getI18nTextForFeaturesHeader } from 'pricing-pages-ui-components/components/merch-card/feature-display/customerPlatformFeatureDisplayI18nUtils';
import { CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES } from 'pricing-pages-ui-components/constants/skuCategoryNames';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { ProductIcon } from 'pricing-pages-ui-components/productIcon/ProductIcon';
import { useCallback } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { VALID_CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES } from 'pricing-pages-ui-components/constants/skuCategoryNames';
import { useIsRevenueHubEnabled } from '../../../hooks/useIsRevenueHubEnabled';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function isValidProductIconName(name) {
  return VALID_CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES.has(name);
}
function getFreeHubLabel(name, isRevenueHubEnabled) {
  switch (name) {
    case 'MARKETING_PRODUCTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.marketing"
      });
    case 'SALES_PRODUCTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.sales"
      });
    case 'SERVICE_HUB_PRODUCTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.service"
      });
    case 'CMS_HUB_PRODUCTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.content"
      });
    case 'OPERATIONS_HUB_PRODUCTS':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.data"
      });
    case 'COMMERCE_PRODUCTS':
      return isRevenueHubEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.revenue"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicPricingPage.merchCard.freeFeatures.commerce"
      });
    default:
      return null;
  }
}
function FreeToolsFeaturesList({
  isCollapsible,
  isRevenueHubEnabled
}) {
  return /*#__PURE__*/_jsx(FeaturesDisplayLayoutUI, {
    HeaderSection: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicPricingPage.merchCard.freeFeatures.includes"
    }),
    FeaturesListSection: /*#__PURE__*/_jsx(List, {
      children: CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES.map((name, index) => /*#__PURE__*/_jsxs(Flex, {
        marginBlockStart: index > 0 ? '300' : undefined,
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(ProductIcon, {
          name: name,
          iconStyle: {
            blockSize: '32px',
            marginInlineEnd: '300'
          }
        }), /*#__PURE__*/_jsx(Text, {
          children: getFreeHubLabel(name, isRevenueHubEnabled)
        })]
      }, name))
    }),
    isCollapsible: isCollapsible
  });
}
function BrandingAndLimitPriorityFeaturesWithLink({
  brandingAndLimitFeatures,
  uncategorizedFeatures,
  isCollapsible
}) {
  const history = useHistory();
  const handleClick = useCallback(() => {
    history.push('/crm');
  }, [history]);
  return /*#__PURE__*/_jsx(BrandingAndLimitPriorityFeatures, {
    brandingAndLimitFeatures: brandingAndLimitFeatures,
    uncategorizedFeatures: uncategorizedFeatures,
    handleClickFreeToolsWithLimitsLink: handleClick,
    isCollapsible: isCollapsible,
    isFreePortal: true
  });
}
function getRevenueFeatureItemText(level, isRevenueHubEnabled) {
  if (!isRevenueHubEnabled) {
    return null;
  }
  if (level === 'FREE') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicPricingPage.merchCard.suiteFeatures.free.revenue"
    });
  }
  if (level === 'STARTER') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicPricingPage.merchCard.suiteFeatures.starter.revenue"
    });
  }
  if (level === 'PROFESSIONAL') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicPricingPage.merchCard.suiteFeatures.professional.revenue"
    });
  }
  if (level === 'ENTERPRISE') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicPricingPage.merchCard.suiteFeatures.enterprise.revenue"
    });
  }
  return null;
}
function categorizePriorityFeatures(features) {
  const brandingAndLimit = [];
  const uncategorized = [];
  for (const feature of features) {
    if (typeof feature.featureType === 'string' && (feature.featureType === 'LIMIT_INCREASE' || feature.featureType === 'REMOVE_BRANDING')) {
      brandingAndLimit.push(feature);
    } else {
      uncategorized.push(feature);
    }
  }
  return {
    brandingAndLimit,
    uncategorized
  };
}
export const PublicMerchCardFeatures = ({
  card,
  lowerTierMessage
}) => {
  const isMobile = useIsMobile();
  const isRevenueHubEnabled = useIsRevenueHubEnabled();
  const {
    featureDisplay
  } = card;
  if (featureDisplay.type === 'free') {
    return /*#__PURE__*/_jsx(FreeToolsFeaturesList, {
      isCollapsible: isMobile,
      isRevenueHubEnabled: isRevenueHubEnabled
    });
  }
  if (featureDisplay.type === 'bundledHubs') {
    const sortedProducts = [...featureDisplay.bundledProducts].sort((a, b) => {
      const aIdx = CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES.indexOf(a.skuCategoryName);
      const bIdx = CUSTOMER_PLATFORM_BUNDLE_SKU_CATEGORY_NAMES.indexOf(b.skuCategoryName);
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
    const header = typeof card.productLevel === 'string' ? getI18nTextForFeaturesHeader({
      productLevel: card.productLevel
    }) : undefined;
    const includedContacts = card.packagedQuantitiesMap['CONTACTS'] || null;
    return /*#__PURE__*/_jsx(FeaturesDisplayLayoutUI, {
      HeaderSection: header,
      FeaturesListSection: /*#__PURE__*/_jsx(List, {
        children: sortedProducts.map((bp, index) => {
          var _getRevenueFeatureIte;
          const rawCategory = typeof bp.skuCategoryName === 'string' ? bp.skuCategoryName : '';
          const skuCategory = isValidProductIconName(rawCategory) ? rawCategory : undefined;
          return /*#__PURE__*/_jsxs(Flex, {
            marginBlockStart: index > 0 ? '300' : undefined,
            alignItems: "center",
            children: [skuCategory && /*#__PURE__*/_jsx(ProductIcon, {
              name: skuCategory,
              iconStyle: {
                blockSize: '32px',
                marginInlineEnd: '300'
              }
            }), /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-125",
                children: skuCategory && typeof card.productLevel === 'string' ? skuCategory === 'COMMERCE_PRODUCTS' && isRevenueHubEnabled ? (_getRevenueFeatureIte = getRevenueFeatureItemText(card.productLevel, isRevenueHubEnabled)) !== null && _getRevenueFeatureIte !== void 0 ? _getRevenueFeatureIte : getI18nTextForFeatureItem(card.productLevel, skuCategory) : getI18nTextForFeatureItem(card.productLevel, skuCategory) : bp.productApiName
              }), includedContacts && skuCategory === 'MARKETING_PRODUCTS' && /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "publicPricingPage.merchCard.features.includedContacts",
                options: {
                  count: /*#__PURE__*/_jsx(FormattedNumber, {
                    value: includedContacts
                  })
                }
              })]
            })]
          }, bp.productApiName);
        })
      }),
      isCollapsible: isMobile
    });
  }
  const {
    brandingAndLimit,
    uncategorized
  } = categorizePriorityFeatures(featureDisplay.features);
  if (brandingAndLimit.length > 0) {
    return /*#__PURE__*/_jsx(BrandingAndLimitPriorityFeaturesWithLink, {
      brandingAndLimitFeatures: brandingAndLimit,
      uncategorizedFeatures: uncategorized,
      isCollapsible: isMobile
    });
  }
  return /*#__PURE__*/_jsx(UncategorizedPriorityFeatures, {
    uncategorizedFeatures: uncategorized,
    lowerTierMessage: lowerTierMessage,
    isCollapsible: isMobile
  });
};